import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rss, HeartHandshake, ScrollText, Loader2, UserCircle2, HandHeart, Gift, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GiftPremiumModal } from "@/components/GiftPremiumModal";

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function Feed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState(null);
  const [giftTo, setGiftTo] = useState(null);

  useEffect(() => {
    api.get("/feed").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  const react = async (id) => {
    try {
      const { data } = await api.post(`/feed/${id}/react`);
      setPosts((prev) => prev.map((p) => (p.id === id ? { ...p, reacted: data.reacted, reactions: data.reactions } : p)));
    } catch {
      toast.error("Could not save your reaction. Please try again.");
    }
  };

  const thank = async (id) => {
    try {
      const { data } = await api.post(`/feed/${id}/thanks`);
      setPosts((prev) => prev.map((p) => (p.id === id ? { ...p, thanked: true, thanks_text: data.thanks_text } : p)));
      toast.success("Your thank-you has been posted.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send your thank-you.");
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 sm:px-8 pt-12 sm:pt-16 pb-24" data-testid="feed-page">
      <div className="flex items-center gap-3 mb-2">
        <Rss className="w-6 h-6 text-[#2C2A29]" strokeWidth={1.5} />
        <p className="font-body text-sm uppercase tracking-[0.2em] text-[#7A756E]">Newsfeed</p>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight">The community wall</h1>
      <p className="font-decree text-lg text-[#7A756E] mt-3">Public decrees and prayer requests from believers across the app.</p>

      <div className="flex flex-wrap gap-3 mt-6">
        <Link to="/prayer" data-testid="feed-cta-prayer" className="px-4 py-2.5 text-sm border border-[#D9D4CC] rounded-sm text-[#2C2A29] hover:bg-[#EAE6DF] transition-colors duration-300 flex items-center gap-2">
          <HeartHandshake className="w-4 h-4" /> Share a prayer
        </Link>
        <Link to="/generate" data-testid="feed-cta-decree" className="px-4 py-2.5 text-sm border border-[#D9D4CC] rounded-sm text-[#2C2A29] hover:bg-[#EAE6DF] transition-colors duration-300 flex items-center gap-2">
          <ScrollText className="w-4 h-4" /> Post a public decree
        </Link>
      </div>

      {posts === null ? (
        <div className="py-20 text-center"><Loader2 className="w-7 h-7 text-[#2C2A29] animate-spin mx-auto" /></div>
      ) : posts.length === 0 ? (
        <p className="font-decree text-xl text-[#4A4744] mt-12" data-testid="feed-empty">Nothing here yet. Be the first to share.</p>
      ) : (
        <ul className="mt-8 space-y-5" data-testid="feed-list">
          {posts.map((p) => (
            <li key={p.id} data-testid={`feed-item-${p.id}`} className="border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm p-6">
              <div className="flex items-center gap-2.5">
                <UserCircle2 className="w-5 h-5 text-[#B7AE9F]" strokeWidth={1.5} />
                <span className="font-body text-sm text-[#2C2A29]">{p.anonymous ? "Anonymous" : (p.name || "A believer")}</span>
                <span className="font-body text-xs text-[#B7AE9F]">· {timeAgo(p.created_at)}</span>
                <span className={`ml-auto text-xs uppercase tracking-wide px-2.5 py-1 rounded-full ${p.type === "decree" ? "bg-[#F3F0EA] text-[#9B7E4E]" : p.type === "gift" ? "bg-[#9B7E4E] text-[#FAF9F6]" : "border border-[#D9D4CC] text-[#7A756E]"}`}>
                  {p.type === "decree" ? "Decree" : p.type === "gift" ? "Gift" : "Prayer"}
                </span>
              </div>

              {p.type === "gift" ? (
                <div className="mt-4" data-testid={`gift-post-${p.id}`}>
                  <p className="font-heading text-xl text-[#2C2A29] flex items-center gap-2">
                    <Gift className="w-5 h-5 text-[#9B7E4E]" strokeWidth={1.5} />
                    {p.name} blessed {p.recipient_name} with a month of Premium
                  </p>
                  {p.message && <p className="font-decree text-lg text-[#4A4744] mt-3 pl-4 border-l-2 border-[#9B7E4E] italic">“{p.message}”</p>}
                  {p.thanked && (
                    <p className="font-decree text-base text-[#2C2A29] mt-4 flex items-start gap-2" data-testid={`gift-thanks-${p.id}`}>
                      <HandHeart className="w-4 h-4 mt-1 text-[#9B7E4E] shrink-0" strokeWidth={1.75} />
                      <span><span className="font-body text-xs uppercase tracking-wide text-[#7A756E] mr-2">{p.recipient_name}</span>{p.thanks_text}</span>
                    </p>
                  )}
                </div>
              ) : p.type === "prayer" ? (
                <p className="font-decree text-lg text-[#2C2A29] mt-4 leading-relaxed whitespace-pre-wrap">{p.message}</p>
              ) : (
                <div className="mt-4">
                  <p className="font-heading text-xl text-[#2C2A29] capitalize">{p.topic}</p>
                  <ol className="mt-3 space-y-3">
                    {(p.decrees || []).map((d, i) => (
                      <li key={i} className="font-decree text-base text-[#2C2A29] leading-relaxed">
                        {d.text}
                        <span className="block font-body text-xs uppercase tracking-wide text-[#9B7E4E] mt-1">{d.reference}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              <div className="mt-5 pt-4 border-t border-[#EAE6DF] flex items-center gap-2 flex-wrap">
                <button
                  data-testid={`react-button-${p.id}`}
                  onClick={() => react(p.id)}
                  aria-pressed={p.reacted}
                  className={`flex items-center gap-2 px-4 py-2 text-sm rounded-full border transition-all duration-300 ${
                    p.reacted
                      ? "bg-[#9B7E4E] text-[#FAF9F6] border-[#9B7E4E]"
                      : "border-[#D9D4CC] text-[#4A4744] hover:bg-[#EAE6DF] hover:border-[#B7AE9F]"
                  }`}
                >
                  <HandHeart className={`w-4 h-4 transition-transform duration-200 ${p.reacted ? "scale-110" : ""}`} strokeWidth={1.75} />
                  {p.type === "prayer" ? "I'm praying" : "Amen"}
                  {p.reactions > 0 && (
                    <span data-testid={`react-count-${p.id}`} className="font-body text-xs">· {p.reactions}</span>
                  )}
                </button>
                {p.type === "gift" && p.is_recipient && !p.thanked && (
                  <button
                    data-testid={`thanks-button-${p.id}`}
                    onClick={() => thank(p.id)}
                    className="ml-auto flex items-center gap-2 px-4 py-2 text-sm rounded-full bg-[#2C2A29] text-[#FAF9F6] hover:opacity-90 transition-opacity duration-300"
                  >
                    <Sparkles className="w-4 h-4" strokeWidth={1.75} /> Say thank you
                  </button>
                )}
                {p.user_id && user && p.user_id !== user.id && p.type !== "gift" && (
                  <button
                    data-testid={`gift-button-${p.id}`}
                    onClick={() => setGiftTo({ id: p.user_id, name: p.name })}
                    className="ml-auto flex items-center gap-2 px-4 py-2 text-sm rounded-full border border-[#D9D4CC] text-[#9B7E4E] hover:bg-[#F3F0EA] hover:border-[#9B7E4E] transition-all duration-300"
                  >
                    <Gift className="w-4 h-4" strokeWidth={1.75} /> Gift Premium
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {giftTo && <GiftPremiumModal recipient={giftTo} onClose={() => setGiftTo(null)} />}
    </div>
  );
}
